/**
 * WlDunningSettingsCard — configuração de inadimplência (Fase F Uaizy v1).
 *
 * Permite ao operador WL definir:
 *  - Dias até bloquear módulos pagos (past_due → bloqueio progressivo).
 *  - Dias até suspender o tenant inadimplente (past_due → suspended).
 *
 * Toda a leitura/escrita passa pelas RPCs `wl_get_dunning_settings` e
 * `wl_update_dunning_settings` (SECURITY DEFINER + wl_assert_permission).
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save } from "lucide-react";
import { useWlDunningSettings, useUpdateWlDunningSettings } from "@/hooks/wl/useWlDunningSettings";
import { showToast } from "@/lib/toast";

export default function WlDunningSettingsCard() {
  const { data, isLoading } = useWlDunningSettings();
  const update = useUpdateWlDunningSettings();

  const [block, setBlock] = useState<number>(7);
  const [suspend, setSuspend] = useState<number>(30);

  useEffect(() => {
    if (data) {
      setBlock(data.block_after_days || 7);
      setSuspend(data.suspend_after_days || 30);
    }
  }, [data]);

  const submit = async () => {
    if (!Number.isFinite(block) || block < 1 || block > 365) {
      showToast.error("Dias para bloquear módulos deve estar entre 1 e 365");
      return;
    }
    if (!Number.isFinite(suspend) || suspend < 1 || suspend > 365) {
      showToast.error("Dias para suspender deve estar entre 1 e 365");
      return;
    }
    if (suspend < block) {
      showToast.error("Dias para suspender deve ser maior ou igual aos dias para bloquear");
      return;
    }
    try {
      await update.mutateAsync({ block_after_days: block, suspend_after_days: suspend });
      showToast.success("Parâmetros de inadimplência atualizados");
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao salvar parâmetros");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inadimplência</CardTitle>
        <CardDescription>
          Parâmetros usados pelo enforcement automático do gateway Uaizy (integração v1).
          Tenants com cobranças vencidas seguem esta linha do tempo até a suspensão.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Carregando…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="dunning-block">Bloquear módulos após (dias)</Label>
                <Input
                  id="dunning-block"
                  type="number"
                  min={1}
                  max={365}
                  value={block}
                  onChange={(e) => setBlock(parseInt(e.target.value) || 0)}
                />
                <p className="text-[11px] text-muted-foreground">
                  Inboxes e instâncias continuam liberados; módulos pagos premium passam a exibir aviso de regularização.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dunning-suspend">Suspender tenant após (dias)</Label>
                <Input
                  id="dunning-suspend"
                  type="number"
                  min={1}
                  max={365}
                  value={suspend}
                  onChange={(e) => setSuspend(parseInt(e.target.value) || 0)}
                />
                <p className="text-[11px] text-muted-foreground">
                  A partir deste limite o tenant é suspenso: inboxes, instâncias e módulos premium ficam bloqueados.
                </p>
              </div>
            </div>

            <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                  Plano padrão
                </Badge>
                {data?.default_new_tenant_plan_id ? (
                  <span>
                    <span className="font-medium text-foreground">{data.default_plan_name || data.default_new_tenant_plan_id}</span>{" "}
                    será provisionado em trial para novos tenants.
                  </span>
                ) : (
                  <span>Nenhum plano marcado como padrão — novos tenants não recebem trial automático.</span>
                )}
              </div>
              <p>
                Para definir o plano padrão, abra a página de Planos e marque a opção
                <em> “Plano padrão de novos tenants” </em>
                no plano desejado.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={submit} disabled={update.isPending}>
                {update.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
